import { cn } from "@tcg/ui";
import { CircleCheck, CirclePause, LoaderCircle, TriangleAlert } from "lucide-react";
import { useTranslation } from "react-i18next";

import { MoneyText } from "@/components/MoneyText";

import { ItemBadges } from "./ItemBadges";
import { clockTime, localized, type GradingInfo, type LocalizedText } from "./labels";

export interface TileItem {
  variantId: string;
  title: LocalizedText;
  variantTitle?: string | LocalizedText | null;
  productType: string;
  rawRank?: string | null;
  grading?: GradingInfo | null;
  certNumber?: string | null;
  price: number;
  available: boolean;
  quantity: number;
  reason?: string | null;
  isConsigned: boolean;
  thumb?: string | null;
}

/**
 * A big search-result tile (design/05 §3.1): picture, name, Rank / 「PSA 10」 / 「未開封」,
 * price 税込, and the stock state in words + icon (● 在庫あり / ⚠ 販売済 / ⏸ 保留中).
 * Tap = add to the basket (disabled when it cannot be sold, with the reason).
 *
 * R21 (T07-40): `goneAt` = the live stream said it was sold / held elsewhere at that time → a red
 * bar 「10:02に売れました」 under the tile and the button closed at once; `checking` = the stream is
 * down or being caught up → a one-of-a-kind card waits 「在庫を確認中…」.
 */
export function ResultTile({
  item,
  onAdd,
  disabled,
  goneAt = null,
  checking = false,
}: {
  item: TileItem;
  onAdd: (item: TileItem) => void;
  disabled?: boolean;
  goneAt?: string | null;
  checking?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? "ja";
  const title = localized(item.title, locale);
  const sub =
    typeof item.variantTitle === "string"
      ? item.variantTitle
      : localized(item.variantTitle, locale);
  const held = !item.available && item.reason === "reserved";
  const state = checking
    ? {
        icon: <LoaderCircle aria-hidden className="size-4" />,
        text: t("tile.checking"),
        cls: "text-pos-wait",
      }
    : item.available
      ? {
          icon: <CircleCheck aria-hidden className="size-4" />,
          text:
            item.productType === "sealed"
              ? t("tile.in_stock_qty", { count: item.quantity })
              : t("tile.in_stock"),
          cls: "text-pos-ok",
        }
      : held
        ? {
            icon: <CirclePause aria-hidden className="size-4" />,
            text: t("tile.held"),
            cls: "text-pos-wait",
          }
        : {
            icon: <TriangleAlert aria-hidden className="size-4" />,
            text: t(`reason.${item.reason ?? "sold"}`, { defaultValue: t("tile.sold") }),
            cls: "text-pos-problem",
          };
  const gone = goneAt && !item.available ? clockTime(goneAt, locale) : null;
  const button = (
    <button
      type="button"
      onClick={() => onAdd(item)}
      disabled={disabled || checking || !item.available}
      aria-label={`${title} ${sub}`.trim()}
      data-live={gone ? "gone" : checking ? "checking" : undefined}
      className={cn(
        "flex min-h-24 w-full items-stretch gap-3 rounded-lg border bg-background p-2 text-left transition-colors hover:bg-accent disabled:cursor-not-allowed",
        (!item.available || checking) && "opacity-70",
        gone && "rounded-b-none border-pos-problem",
      )}
    >
      <span className="flex size-20 shrink-0 items-center justify-center overflow-hidden rounded-md bg-muted">
        {item.thumb ? (
          <img src={item.thumb} alt="" loading="lazy" className="size-full object-contain" />
        ) : null}
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="line-clamp-2 text-base font-medium">{title}</span>
        {sub ? <span className="truncate text-sm text-muted-foreground">{sub}</span> : null}
        <ItemBadges
          productType={item.productType}
          rawRank={item.rawRank}
          grading={item.grading}
          consigned={item.isConsigned}
          size="sm"
        />
        <span className="mt-auto flex items-center justify-between gap-2">
          <span>
            <MoneyText amount={item.price} size="lg" />{" "}
            <span className="text-xs text-muted-foreground">{t("tile.tax_included")}</span>
          </span>
          <span className={cn("inline-flex items-center gap-1 text-sm font-medium", state.cls)}>
            {state.icon}
            {state.text}
          </span>
        </span>
      </span>
    </button>
  );
  // one element tree either way: the button stays the same node when the red bar appears
  return (
    <div>
      {button}
      {gone ? (
        <p
          role="alert"
          className="flex items-center gap-1 rounded-b-lg bg-pos-problem px-3 py-1 text-sm font-semibold text-white"
        >
          <TriangleAlert aria-hidden className="size-4" />
          {item.reason === "reserved"
            ? t("tile.held_live", { when: gone })
            : t("tile.sold_live", { when: gone })}
        </p>
      ) : null}
    </div>
  );
}
